import { useQuery, useMutation, useTransport, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'

import { getBooking, updateBooking, updateBookingQuestionnaire, createBooking, listEvents, listInvoices, listQuotes, getDashboardSummary, listJobEvents } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetBookingRequestSchema, GetBookingResponseSchema } from '@/gen/cortado/v1/cortado_pb'
import {
  prepareOptimisticUpdate,
  rollbackOptimisticUpdate,
  type OptimisticContext,
} from '@/lib/query-utils'

/**
 * Hook to fetch a single booking by ID
 *
 * This hook:
 * - Fetches a fully enriched booking with all ID references
 * - Includes note_ids, task_ids, event_ids, invoice_ids
 * - Includes audit users (created_by_user, updated_by_user)
 * - Returns calculated aggregations (totals, counts)
 * - Follows "Stand on Their Own in Full" principle - IDs only, use separate queries for full objects
 *
 * @param bookingId - The ID of the booking to fetch
 * @returns Query result with booking, loading state, and error
 */
export function useBooking(bookingId?: string) {
  const query = useQuery(
    getBooking,
    create(GetBookingRequestSchema, {
      id: bookingId ?? '',
    }),
    {
      enabled: !!bookingId,
      select: (data) => data?.booking,
    }
  )

  return {
    booking: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to update an existing booking
 *
 * This hook provides mutation capabilities for updating bookings with field mask support.
 *
 * **Updatable Fields:**
 * - stage - BookingStageEnum (e.g., CONFIRMED, IN_PROGRESS, COMPLETED)
 * - event_type - Organization event type key (e.g., 'Wedding', 'Corporate')
 *
 * **Field Mask:**
 * Always include a field mask in UpdateBookingRequest to specify which fields to update.
 * This ensures precise control over what changes.
 *
 * **Location:**
 * Location lives on the event, not the booking — use `useUpdateEvent` with
 * `event_location`. The booking's event_location field is retired.
 *
 * **Cache Management:**
 * - Automatically updates the individual booking cache with the latest data
 * - Ensures immediate UI feedback
 *
 * @returns Mutation result with updateBooking function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { updateBooking } = useUpdateBooking()
 *
 * // Update stage only
 * await updateBooking(create(UpdateBookingRequestSchema, {
 *   id: booking.id,
 *   stage: BookingStageEnum.CONFIRMED,
 *   updateMask: create(FieldMaskSchema, {
 *     paths: ['stage']
 *   })
 * }))
 * ```
 */
export function useUpdateBooking() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    updateBooking,
    {
      onMutate: async (variables) => {
        // Cancel in-flight queries and snapshot for rollback
        const queryKey = createConnectQueryKey({
          schema: getBooking,
          transport,
          input: { id: variables.id },
          cardinality: 'finite',
        })
        return await prepareOptimisticUpdate({ queryClient, queryKey })
      },

      onError: (_error, _variables, context) => {
        // Rollback on error
        rollbackOptimisticUpdate({ queryClient, context: context as OptimisticContext })
      },

      onSuccess: (response) => {
        const updatedBooking = response.booking

        if (!updatedBooking) return

        // Update individual booking cache
        const queryKey = createConnectQueryKey({
          schema: getBooking,
          transport,
          input: { id: updatedBooking.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          queryKey,
          create(GetBookingResponseSchema, { booking: updatedBooking })
        )
      },

      onSettled: () => {
        // Invalidate related list queries that may reference booking data
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInvoices,
            transport,
            cardinality: undefined,
          }),
        })
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listQuotes,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    updateBooking: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to replace a booking's questionnaire with a staff-authored list of
 * items.
 *
 * Unlike `useUpdateBooking`, this RPC does not accept a field mask — the
 * request's `items` is always the full desired list, in order. Answers are
 * server-owned: the server ignores any answer content on the request and
 * carries over existing answers per `ReconcileBookingQuestionnaire`.
 *
 * **Cache Management:**
 * - Updates the individual booking cache with the server-reconciled snapshot
 *   (including carried-over answers) so the event screen reflects it
 *   immediately.
 *
 * @returns Mutation result with updateBookingQuestionnaire function
 */
export function useUpdateBookingQuestionnaire() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    updateBookingQuestionnaire,
    {
      onSuccess: (response) => {
        const updatedBooking = response.booking

        if (!updatedBooking) return

        const queryKey = createConnectQueryKey({
          schema: getBooking,
          transport,
          input: { id: updatedBooking.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          queryKey,
          create(GetBookingResponseSchema, { booking: updatedBooking })
        )
        // The job's Logistics readiness counts the questionnaire.
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listJobEvents, transport, cardinality: undefined }),
        })
      },
    }
  )

  return {
    mutation,
    updateBookingQuestionnaire: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to create a new booking directly (without requiring an inquiry)
 *
 * This hook provides mutation capabilities for creating bookings with:
 * - Required contact_id (booking must be associated with a contact)
 * - Optional event_type (location belongs on the event, not the booking)
 *
 * **Use Cases:**
 * - Creating a booking directly from the events page
 * - Quick booking creation workflow without going through inquiries
 *
 * **Cache Management:**
 * - Invalidates listEvents queries to refresh event lists
 * - Updates individual booking cache with the new booking
 *
 * @returns Mutation result with createBooking function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { createBooking, isLoading } = useCreateBooking()
 *
 * // Create a new booking
 * const response = await createBooking(create(CreateBookingRequestSchema, {
 *   organizationId: org.id,
 *   contactId: selectedContact.id,
 *   eventType: 'Wedding',
 * }))
 *
 * // Use the booking ID for subsequent operations
 * const bookingId = response.booking?.id
 * ```
 */
export function useCreateBooking() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createBooking,
    {
      onSuccess: (response) => {
        const newBooking = response.booking

        if (!newBooking) return

        // Update individual booking cache
        const queryKey = createConnectQueryKey({
          schema: getBooking,
          transport,
          input: { id: newBooking.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          queryKey,
          create(GetBookingResponseSchema, { booking: newBooking })
        )

        // Invalidate listEvents queries since creating a booking may affect event lists
        // refetchType: 'all' ensures inactive queries (e.g. calendar) are also refetched
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listEvents,
            transport,
            cardinality: undefined,
          }),
          refetchType: 'all',
        })

        // Invalidate the dashboard aggregate so its event cards refresh too
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getDashboardSummary,
            transport,
            cardinality: undefined,
          }),
          refetchType: 'all',
        })
      },
    }
  )

  return {
    mutation,
    createBooking: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to prefetch a booking on hover
 *
 * This hook provides a prefetch function that can be called on hover
 * to load booking data into the cache before the user clicks.
 * Skips prefetching if the data is already in the cache.
 *
 * @returns Prefetch function
 */
export function usePrefetchBooking() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const prefetchBooking = async (bookingId: string) => {
    const request = create(GetBookingRequestSchema, { id: bookingId })
    const queryKey = createConnectQueryKey({
      schema: getBooking,
      transport,
      input: { id: bookingId },
      cardinality: 'finite',
    })

    // Skip prefetch if data is already in cache
    const cachedData = queryClient.getQueryData(queryKey)
    if (cachedData) {
      return
    }

    await queryClient.prefetchQuery(
      createQueryOptions(getBooking, request, {
        transport,
      })
    )
  }

  return { prefetchBooking }
}
